/**
 * IndividualPensionPlanFlowV2
 *
 * Orchestrates the flow for users to declare if they have external
 * individual pension plans. Handles multiple states:
 * - Indexa already connected → quick confirmation
 * - Manual plan registered → auto-skip, or prefilled form in edit mode
 * - Nothing registered → registration flow
 *
 * El backend solo devuelve la declaración del AÑO EN CURSO (filtro por `year`), así que
 * todo plan que llega aquí es de este año: no hace falta comprobar su antigüedad. La
 * heurística de "menos de 30 días" que había antes era un intento de aproximar eso, y
 * fallaba justo al cambiar de año (quien declaraba el 26 de diciembre entraba el 5 de
 * enero con 16 días y se le saltaba la pregunta del año nuevo).
 */
import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';

import { I18nContext } from '../../context/translations/TranslationsContext.context';
import getIndividualPensionPlan from '../../api/users/getIndividualPensionPlan';
import { IndividualPensionPlan } from '../../api/users/interfaces/IndividualPensionPlan';
import getIndexaToken from '../../api/thirdparty/indexa/getIndexaToken';
import IndividualPensionPlanModalV2 from './IndividualPensionPlanModalV2';
import { analytics } from '../../services/analytics';

interface IndividualPensionPlanFlowV2Props {
	isOpen: boolean;
	onClose: () => void;
	onComplete: () => void;
	token: string;
	/**
	 * `auto` (por defecto): el flujo es un paso más de otra tarea, así que a quien ya
	 * declaró este año no se le vuelve a preguntar.
	 * `edit`: el usuario ha pedido expresamente gestionar su plan externo, así que se le
	 * abre el formulario con lo que tuviera declarado, para que pueda corregirlo.
	 */
	mode?: 'auto' | 'edit';
}

type ModalType = 'indexa-only' | 'registration' | null;

const IndividualPensionPlanFlowV2: React.FC<IndividualPensionPlanFlowV2Props> = ({
	isOpen,
	onClose,
	onComplete,
	token,
	mode = 'auto'
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [isLoading, setIsLoading] = useState(true);
	const [existingPlan, setExistingPlan] = useState<IndividualPensionPlan | null>(null);
	const [activeModal, setActiveModal] = useState<ModalType>(null);

	// Tracking refs: detect rising edge de isOpen y si el usuario llegó a guardar.
	const wasOpenRef = useRef(false);
	const savedRef = useRef(false);

	useEffect(() => {
		if (isOpen && !wasOpenRef.current) {
			analytics.externalPlanPanelOpened();
			savedRef.current = false;
		}
		wasOpenRef.current = isOpen;
	}, [isOpen]);

	const checkExistingPlans = useCallback(async () => {
		setIsLoading(true);
		try {
			// 1. Check if user has Indexa integration connected
			const indexaTokenResponse = await getIndexaToken(token);
			const hasIndexa = indexaTokenResponse?.success && indexaTokenResponse?.data?.active;

			// 2. If user has Indexa, skip asking
			if (hasIndexa) {
				setExistingPlan(null);
				setActiveModal('indexa-only');
				setIsLoading(false);
				return;
			}

			// 3. Get manually registered pension plan
			const planResponse = await getIndividualPensionPlan(token);
			const manualPlan = planResponse.success && planResponse.data ? planResponse.data : null;

			setExistingPlan(manualPlan);

			// 4. Decide which modal to show
			const hasDeclaredThisYear = !!(manualPlan && manualPlan.active && manualPlan.hasIndividual);

			if (hasDeclaredThisYear && mode !== 'edit') {
				// Auto-skip: ya declaró este año y solo estamos de paso. El usuario no ha
				// guardado nada, fue el sistema quien continuó: emitimos un evento dedicado
				// en vez de external_plan_saved para no contaminar el numerador del funnel.
				savedRef.current = true;
				analytics.externalPlanAutoSkipped();
				onComplete();
				onClose();
			} else {
				// Sin declaración de este año, o el usuario ha pedido editarla: en ambos
				// casos va al formulario, que se prerrellena solo si hay algo que editar.
				setActiveModal('registration');
			}
		} catch (error) {
			console.error('Error checking individual pension plan:', error);
			setActiveModal('registration');
		} finally {
			setIsLoading(false);
		}
	}, [token, onComplete, onClose, mode]);

	// Se carga UNA vez por apertura. `checkExistingPlans` cambia de identidad en cuanto
	// el padre re-renderiza (sus `onClose`/`onComplete` son arrows inline en las cuatro
	// pantallas que montan el flujo), y volver a cargar pondría `isLoading` a true,
	// desmontando el formulario abierto y tirando lo que el usuario llevara escrito.
	const loadedForCurrentOpenRef = useRef(false);

	useEffect(() => {
		if (!isOpen) {
			loadedForCurrentOpenRef.current = false;
			return;
		}
		if (loadedForCurrentOpenRef.current) return;

		loadedForCurrentOpenRef.current = true;
		checkExistingPlans();
	}, [isOpen, checkExistingPlans]);

	// Único criterio de "hay declaración este año", compartido por el formulario y la
	// analítica: una fila puede existir y ser un "no tengo plan", que no es lo mismo.
	// El backend ya filtra por año y por `active`, así que basta con mirar los flags.
	const declaredThisYear = !!(existingPlan?.active && existingPlan?.hasIndividual);

	const handleRegistrationComplete = () => {
		savedRef.current = true;
		// Corregir un dato ya declarado no es un alta: contarlo como tal inflaría el
		// numerador del funnel de registro con cada edición. Y al revés, una primera
		// declaración sobre un "no tengo plan" sí es un alta.
		analytics.externalPlanSaved({ planType: declaredThisYear ? 'edit' : 'registration' });
		setActiveModal(null);
		onComplete();
		onClose();
	};

	const handleConfirmationProceed = () => {
		savedRef.current = true;
		analytics.externalPlanSaved({ planType: 'existing_confirmed' });
		setActiveModal(null);
		onComplete();
		onClose();
	};

	const handleCancel = () => {
		if (!savedRef.current) {
			analytics.externalPlanPanelClosedWithoutSave();
		}
		setActiveModal(null);
		onClose();
	};

	// CASE 1: Indexa already connected
	if (activeModal === 'indexa-only' && !isLoading) {
		return (
			<Dialog open={true} onOpenChange={(open) => !open && handleCancel()}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<CheckCircle className="w-5 h-5 text-green-500" />
							{translate('user.myContributions.indexaConnectedTitle')}
						</DialogTitle>
						<DialogDescription className="sr-only">
							{translate('benefit.flow.indexaConnectedDesc')}
						</DialogDescription>
					</DialogHeader>

					<div className="py-4">
						<p className="text-sm text-gray-600 mb-4">
							{translate('user.myContributions.indexaConnectedDescription')}
						</p>
						<div className="p-3 bg-green-50 rounded-md border border-green-200">
							<p className="text-xs text-gray-700">
								{translate('user.myContributions.indexaConnectedNote')}
							</p>
						</div>
					</div>

					<DialogFooter className="gap-2 sm:gap-0">
						<Button variant="outline" onClick={handleCancel}>
							{translate('common.cancel')}
						</Button>
						<Button onClick={handleConfirmationProceed} className="bg-primary hover:bg-primary/90">
							{translate('common.continue')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		);
	}

	// CASE 2: formulario. Prerrellenado con lo declarado este año cuando lo hay
	// (edición), en blanco cuando no (alta).
	if (activeModal === 'registration' && !isLoading) {
		return (
			<IndividualPensionPlanModalV2
				isOpen={true}
				token={token}
				initialPlan={declaredThisYear ? existingPlan : null}
				onSave={handleRegistrationComplete}
				onClose={handleCancel}
			/>
		);
	}

	// Loading state
	if (isLoading && isOpen) {
		return (
			<Dialog open={true} onOpenChange={(open) => !open && handleCancel()}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader className="sr-only">
						<DialogTitle>{translate('benefit.flow.loading')}</DialogTitle>
						<DialogDescription>{translate('benefit.flow.verifyingPlans')}</DialogDescription>
					</DialogHeader>
					<div className="py-8 flex flex-col items-center justify-center gap-4">
						<Loader2 className="w-6 h-6 animate-spin text-primary" />
						<p className="text-sm text-gray-600">{translate('common.loading')}...</p>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return null;
};

export default IndividualPensionPlanFlowV2;
